import { Injectable } from '@angular/core';
import {
  divIcon,
  LatLng,
  LayerGroup,
  Marker,
  Polygon,
  Polyline,
} from 'leaflet';
import { hatchStyle } from './hatching';
import {
  MarkupCalloutElement,
  MarkupLineElement,
  MarkupLineStyles,
  MarkupPointElement,
  MarkupPointStyles,
  MarkupPolygonElement,
  MarkupTextElement,
} from './markup.model';

@Injectable({ providedIn: 'root' })
export class MarkupService {
  createTextIcon(
    element: Omit<MarkupTextElement, 'type' | 'geometry' | 'id'>,
    disableEvents = false,
  ) {
    const defaultSize = 12;
    const minSize = 5;
    const size = defaultSize - minSize * 2 + element.size * 2;

    return divIcon({
      html: `
      <span style="
        font-size: ${size}px;
        color: ${element.color};
        font-weight: ${element.style.includes('bold') ? 'bold' : 'normal'};
        font-style: ${element.style.includes('italic') ? 'italic' : 'normal'};
        background-color: ${this.hexToRgba(element.background, element.backgroundOpacity)};
        font-family: ${element.font}
      " class="divicon-text">${element.name || '&lt;text&gt;'}</span>
      `,
      // iconSize: [size, size],
      className: `empty-divicon ${disableEvents ? 'pointer-events-none' : ''}`,
    });
  }

  createPointIcon(
    element: Omit<MarkupPointElement, 'type' | 'geometry' | 'id'>,
    disableEvents = false,
  ) {
    let iconSvg = '';
    switch (element.style) {
      case MarkupPointStyles.circle:
        iconSvg = `
    <circle cx="50" cy="50" r="40"  fill="${element.fill}" />
`;
        break;

      case MarkupPointStyles.star:
        iconSvg = `
      <polygon points="50,10 61,39 92,39 66,59 76,90 50,70 24,90 34,59 8,39 39,39"
               fill="${element.fill}"   />
        `;
        break;

      case MarkupPointStyles.cross:
        iconSvg = `
    <rect x="45" y="10" width="10" height="80"  fill="${element.fill}"/>
    <rect x="10" y="45" width="80" height="10"  fill="${element.fill}"/>
  `;
        break;

      case MarkupPointStyles.square:
        iconSvg = `
    <rect x="10" y="10" width="80" height="80" fill="${element.fill}" />
  `;
        break;
    }

    const defaultSize = 12;
    const minSize = 5;
    const size = defaultSize - minSize * 2 + element.size * 2;

    return divIcon({
      html: `
      <svg   viewBox="10 10 80 80"  xmlns="http://www.w3.org/2000/svg">
      ${iconSvg}
      </svg>
      <span style="
        font-size: ${element.textSize}px;
        color: ${element.color};
        font-weight: ${element.textStyle.includes('bold') ? 'bold' : 'normal'};
        font-style: ${element.textStyle.includes('italic') ? 'italic' : 'normal'};
        background-color: ${element.background};
        font-family: ${element.font}
      " class="divicon-label">${element.name}</span>
      `,
      iconSize: [size, size],
      className: `empty-divicon ${disableEvents ? 'pointer-events-none' : ''}`,
    });
  }

  setLineStyles(
    element: Omit<MarkupLineElement, 'type' | 'geometry' | 'id'>,
    line: Polyline,
  ) {
    line.setStyle({
      opacity: element.opacity / 100,
      weight: element.width + 1,
      color: element.color,
      dashArray: [],
    });
    switch (element.style) {
      case MarkupLineStyles.dash:
        line.setStyle({
          dashArray: [10, 10],
        });
        break;
      case MarkupLineStyles.dot:
        line.setStyle({
          dashArray: [1, 10],
        });
        break;

      case MarkupLineStyles.dashdot:
        line.setStyle({
          dashArray: [10, 10, 1, 10],
        });
        break;

      case MarkupLineStyles.longdashdot:
        line.setStyle({
          dashArray: [25, 10, 1, 10],
        });
        break;
    }
  }

  setPolygonStyles(
    element: Omit<MarkupPolygonElement, 'type' | 'geometry'>,
    polygon: Polygon,
  ) {
    polygon.setStyle({
      weight: element.width,
      fillOpacity: element.opacity / 100,
      opacity: element.opacity / 100,
      color: element.stroke,
      ...hatchStyle(element),
    });
  }

  private isStartHigher(latlngs: LatLng[]) {
    if (!Array.isArray(latlngs) || latlngs.length !== 2) {
      return null;
    }

    const startLat = latlngs[0].lat;
    const endLat = latlngs[1].lat;

    if (startLat > endLat) return true; // Start is higher (north)
    if (startLat < endLat) return false; // Start is lower (south)
    return null; // Equal latitude
  }

  private createCalloutDivIcon(
    element: Omit<MarkupCalloutElement, 'id' | 'type' | 'geometry'>,
    latLngs: LatLng[],
    disableEvents: boolean,
  ) {
    const defaultSize = 12;
    const minSize = 5;
    const size = defaultSize - minSize * 2 + element.size * 2;

    return divIcon({
      html: `
      <span style="
        font-size: ${size}px;
        color: ${element.color};
        background-color: ${this.hexToRgba(element.background, element.backgroundOpacity)};
        opacity: ${element.opacity / 100};
      " class="divicon-callout ${this.isStartHigher(latLngs) ? 'divicon-callout-bottom' : ''}">${element.name || '&lt;text&gt;'}</span>
      `,
      // iconSize: [size, size],
      className: `empty-divicon ${disableEvents ? 'pointer-events-none' : ''}`,
    });
  }

  private updateCalloutPolyline(
    element: Omit<MarkupCalloutElement, 'id' | 'type' | 'geometry'>,
    polyline: Polyline,
  ) {
    polyline.setStyle({
      opacity: element.opacity / 100,
      weight: 2,
      color: 'black',
    });
  }

  createCallout(
    element: Omit<MarkupCalloutElement, 'id' | 'type' | 'geometry'>,
    polyline: Polyline,
    disableEvents = false,
  ) {
    const latLngs = polyline.getLatLngs() as LatLng[];

    const marker = new Marker(latLngs[latLngs.length - 1], {
      icon: this.createCalloutDivIcon(element, latLngs, disableEvents),
    });

    this.updateCalloutPolyline(element, polyline);

    return new LayerGroup([polyline, marker]);
  }

  editCallout(
    element: Omit<MarkupCalloutElement, 'id' | 'type' | 'geometry'>,
    layerGroup: LayerGroup,
    disableEvents = false,
  ) {
    const polyline = layerGroup.getLayers()[0] as Polyline;
    const marker = layerGroup.getLayers()[1] as Marker;

    marker.setIcon(
      this.createCalloutDivIcon(
        element,
        polyline.getLatLngs() as LatLng[],
        disableEvents,
      ),
    );
    this.updateCalloutPolyline(element, polyline);
  }

  editCalloutPosition(polyline: Polyline, marker: Marker) {
    if (this.isStartHigher(polyline.getLatLngs() as LatLng[])) {
      marker.getElement()?.children[0].classList.add('divicon-callout-bottom');
    } else {
      marker
        .getElement()
        ?.children[0].classList.remove('divicon-callout-bottom');
    }
  }

  /**
   * Convert hex color to rgba
   * @param hex - '#ff0000'
   * @param opacity - Opacity value from 0-100
   * @returns 'rgba(255, 0, 0, 0.5)'
   */
  private hexToRgba(hex: string, opacity: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  }
}
